import { Injectable, Inject, Injector, inject, signal, computed, DestroyRef } from '@angular/core';
import { ScaleLine } from 'ol/control';
import MousePosition from 'ol/control/MousePosition';
import * as olCoordinate from 'ol/coordinate';

import { createStringXY } from 'ol/coordinate';
import { MapService } from './map.service';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { TT_LIB_CONFIG } from '../tt-lib.config.token';
import { AppConfig, mouseProjection } from '../interfaces/app-config'
import * as olProj from 'ol/proj';
import { BehaviorSubject, Subject, Subscription, Observable, asapScheduler } from 'rxjs';
import { MapindexService } from './mapindex.service';
import { unByKey } from 'ol/Observable';
import { HttpClient } from '@angular/common/http';
import { EventsKey } from 'ol/events';


@Injectable({
  providedIn: 'root'
})
export class ControlService {

  private mouseProjections: mouseProjection[];

  private readonly _mapBrowserEvent = signal<MapBrowserEvent | null>(null);
  readonly mapBrowserEvent = this._mapBrowserEvent.asReadonly();

  private readonly _currentProj = signal<mouseProjection | null>(null);
  readonly currentProj = this._currentProj.asReadonly();

  private readonly _selectedProj = signal<mouseProjection | null>(null);
  readonly selectedProj = this._selectedProj.asReadonly();

  private mouseListeners = new Map<string, EventsKey>();
  private leaveHandlers = new Map<string, () => void>();
  private zoomAllExtent: number[];

  static readonly utmRegex: RegExp = /EPSG:326[0-9]{2}/;
  private utmZone = signal<number>(31);;
  private sheetsInMouseProjection: boolean;

  private projectionsReadySubject = new BehaviorSubject<boolean>(false);
  public projectionsReady$: Observable<boolean>;
  public mapindexService: MapindexService;
  private ttLibConfig = inject<AppConfig>(TT_LIB_CONFIG)
  private readonly destroyRef = inject(DestroyRef);

  /** Calculates the UTM zone based on the current mouse longitude */
  readonly activeUtmZone = computed(() => {
    const event = this._mapBrowserEvent();
    if (!event) return 31; // Default zone
    const lonLat = olProj.transform(
      event.coordinate,
      event.map.getView().getProjection(),
      'EPSG:4326'
    );
    return Math.floor(((lonLat[0] + 180) / 6) % 60) + 1;
  });

  /** choosen projection */
  readonly activeProj = computed(() => {
    const selected = this._selectedProj();
    if (!selected) return null;
    // it's a UTM projection, update the EPSG based on the zone
    if (ControlService.utmRegex.test(selected.epsg)) {
      const zone = this.activeUtmZone();
      return {
        ...selected,
        epsg: `EPSG:326${zone.toString().padStart(2, '0')}`,
        utmZone: zone // adding this for UI convenience
      };
    }
    return selected;
  });

  /** The string for the UI */
  readonly formattedCoords = computed(() => {
    const event = this._mapBrowserEvent();
    const proj = this.activeProj();
    if (!event || !proj) return '';
    if (proj.epsg === 'mapindex') {
      return this.getCellNameFromCoord(event);
    }
    const sourceProj = event.map.getView().getProjection();
    const transformed = olProj.transform(event.coordinate, sourceProj, proj.epsg);
    return this.coordFormatter(transformed, proj);
  })

  constructor(
    private http: HttpClient,
    private mapService: MapService,
    private injector: Injector
  ) {
    this.sheetsInMouseProjection = this.ttLibConfig.map.controls.mousePosition.sheetsInMouseProjection;
    if (this.sheetsInMouseProjection) { this.mapindexService = injector.get<MapindexService>(MapindexService); }
    this.projectionsReady$ = this.projectionsReadySubject.asObservable();
    this.destroyRef.onDestroy(() => this.detachAllTracking());
  }

  initMouseProjections(mapid?): Observable<boolean> {
    this.mouseProjections = this.ttLibConfig.map.controls.mousePosition.mouseProjections;
    if (this.sheetsInMouseProjection) {
      //load geojsons grids for sheet display
      this.mapindexService.initMapindex()
        .subscribe(() => {
          const item = { epsg: 'mapindex', precision: 0, format: '', name: 'mapindex' };
          if (!this.mouseProjections.some(p => p.epsg === item.epsg)) {
            this.mouseProjections.push(item);
          }
          queueMicrotask(() => { this.gogogo();});
        });
    }
    else {
      queueMicrotask(() => { this.gogogo(); });
    }
    return this.projectionsReady$;
  }

  private gogogo() {
    // set init projection: in localstorage or first in list
    if (localStorage.getItem("mouseProjection")) {
      let epsg = JSON.parse(window.localStorage.getItem("mouseProjection")).epsg;
      //case utm
      if (ControlService.utmRegex.test(epsg)) {
        this.utmZone.set(Number(epsg.substring(8, 10)));
      }
      //find index 
      let index = this.mouseProjections.findIndex(x => x.epsg === epsg);
      this.setMousePositionProjection(this.mouseProjections[(index > -1) ? index : 0]);
    } else {
      this.setMousePositionProjection(this.mouseProjections[0]);
    }
    // utm proj change dynamically, sjek if utm in projectionlist
    let epsgs = this.mouseProjections.map((epsg) => { return epsg['epsg'] });
    this.projectionsReadySubject.next(true);
  }

  attachMouseTracking(mapId: string) {
    const map = this.mapService.getMap(mapId);
    if (!map) return;
    const key = map.on('pointermove', (e: any) => {
      if (!e.dragging) {
        queueMicrotask(() => this._mapBrowserEvent.set(e));
      }
    });
    this.mouseListeners.set(mapId, key);
    const viewport = map.getViewport();
    const leaveHandler = () => queueMicrotask(() => this._mapBrowserEvent.set(null));
    viewport.addEventListener('mouseleave', leaveHandler);
    this.leaveHandlers.set(mapId, leaveHandler);
  }

detachMouseTracking(mapId: string) {
    const key = this.mouseListeners.get(mapId);
    if (key) {
      unByKey(key);
      this.mouseListeners.delete(mapId);
      // Clean up listener
      const map = this.mapService.getMap(mapId);
      const handler = this.leaveHandlers.get(mapId);
      if (map && handler) {
        map.getViewport().removeEventListener('mouseleave', handler);
        this.leaveHandlers.delete(mapId);
      }
    }
}

  detachAllTracking() {
    this.mouseListeners.forEach((key) => unByKey(key));
    this.mouseListeners.clear();
  }

  getProjections(): mouseProjection[] {
    return this.mouseProjections;
  }

  addMapIndexLayer(mapId?) {
    if (this.sheetsInMouseProjection) {
      this.mapindexService.mapindexReady$.subscribe(_ => this.mapindexService.addMapindexLayer(mapId));
    }
  }

  setZoomAllExtent(extent: number[]): void {
    this.zoomAllExtent = extent;
  }

  getZoomAllExtent(): number[] {
    return this.zoomAllExtent;
  }

  addScaleLine(units: any, steps: number): void {
    this.mapService.getMap().addControl(
      new ScaleLine({
        units: units,
        steps: steps,
      })
    );
  }

  private coordFormatter(coord: number[], proj: mouseProjection): string {
    let prefix = '';
    if (ControlService.utmRegex.test(proj.epsg)) {
      prefix = `${this.activeUtmZone()}N `;
    }

    if (proj.epsg === 'EPSG:4258') {
      coord = [...coord].reverse();
    }

    return proj.format === 'HDMS'
      ? olCoordinate.toStringHDMS(coord, proj.precision)
      : prefix + olCoordinate.toStringXY(coord, proj.precision);
  }

  setMousePositionProjection(projection: mouseProjection) {
    if (projection.epsg === 'mapindex') {
      if (this.mapindexService.mapindexLayer) { this.mapindexService.mapindexLayer.setVisible(true); }
      this._selectedProj.set(projection);
      localStorage.setItem('mouseProjection', JSON.stringify(projection));
      return;
    }
    // leaving sheet mode
    this.mapindexService?.mapindexLayer?.setVisible(false);
    this._selectedProj.set(projection);
    localStorage.setItem('mouseProjection', JSON.stringify(projection));
  }

  getCellNameFromCoord(evt: MapBrowserEvent) {
    let sheet, cellName;
    if (sheet !== null) { sheet = null; }
    evt.map.forEachFeatureAtPixel(evt.pixel, (f) => {
      cellName = this.mapindexService.getCellNameFromFeatureAndCoordinate(f, evt.coordinate);
    },
      {
        layerFilter: (l) => {
          // return (l.get('customId') === 'mapindexLvl_0_Layer' || l.get('customId') === 'mapindexLvl_1_Layer' || l.get('customId') === 'mapindexLvl_2_Layer');
          return (l.get('customId') === 'mapindexLvl_1_Layer');
        }
      }
    );
    return cellName;
  }

}
